/**
 * The ribbon, as data.
 *
 * THIS FILE IS THE ONLY PLACE THE RIBBON'S CONTENT IS DEFINED. The renderer
 * (`shell/Ribbon.tsx`) knows nothing about OpenADS: it walks `RIBBON_TABS`,
 * resolves icon names through a lookup, evaluates `disabled`/`hidden` against
 * the current `RibbonContext`, and dispatches `action` on the action bus.
 *
 * Adding a v2 tab therefore means: append an object to `RIBBON_TABS`, register
 * a handler for each new `action`, and (optionally) register a tab module in
 * `shell/tabRegistry.ts`. No renderer change, ever. If you find yourself
 * wanting to special-case a button inside the renderer, add a field to
 * `RibbonItem` in `ribbon/types.ts` instead so every future tab inherits it.
 *
 * Icon strings are lucide-react component names. Unknown names degrade to a
 * generic icon rather than crashing — see `shell/icons.ts`.
 */

import { toolRibbonTabs } from "@/tools/catalog";
import type { RibbonContext, RibbonItem, RibbonTab } from "./types";

// ------------------------------------------------------------ predicates ---
// Named so the intent reads in the config table rather than being re-derived
// at each call site.

const noCase = (ctx: RibbonContext) => !ctx.hasCase;
const noResults = (ctx: RibbonContext) => !ctx.hasResults;
/** Run needs a case, an idle queue and a live Docker daemon. */
const cannotRun = (ctx: RibbonContext) => !ctx.hasCase || ctx.jobRunning || !ctx.engineReady;
const notRunning = (ctx: RibbonContext) => !ctx.jobRunning;
const always = () => true;

// ------------------------------------------------------------- shortcuts ---

const RUN: RibbonItem = {
  id: "exec.run",
  label: "Run",
  icon: "Play",
  size: "large",
  action: "exec.run",
  tooltip: "Run the pipeline for the current case",
  disabled: cannotRun,
};

const STOP: RibbonItem = {
  id: "exec.stop",
  label: "Stop",
  icon: "Square",
  size: "large",
  action: "exec.stop",
  tooltip: "Stop the running container",
  disabled: notRunning,
};

const CORE_TABS: RibbonTab[] = [
  // ------------------------------------------------------------------ File ---
  {
    id: "file",
    label: "File",
    groups: [
      {
        id: "file.project",
        label: "Project",
        items: [
          {
            id: "file.openCase",
            label: "Open Study",
            icon: "FolderOpen",
            size: "large",
            action: "file.openCase",
            tooltip: "One subject, a folder of subjects, or a case you saved earlier",
          },
          {
            id: "file.newCase",
            label: "New Analysis",
            icon: "FilePlus",
            size: "large",
            action: "file.newCase",
            tooltip: "Start from an empty protocol",
          },
          {
            id: "file.openRecent",
            label: "Open Recent",
            icon: "History",
            size: "small",
            action: "case.openRecent",
          },
          {
            id: "file.saveCase",
            label: "Save Case",
            icon: "Save",
            size: "small",
            action: "file.saveCase",
            disabled: noCase,
          },
          {
            id: "file.saveCaseAs",
            label: "Save Case As",
            icon: "SaveAll",
            size: "small",
            action: "case.saveAs",
            disabled: noCase,
          },
        ],
      },
      {
        id: "file.import",
        label: "Import",
        items: [
          {
            id: "file.importNifti",
            label: "Import NIfTI",
            icon: "FileInput",
            size: "large",
            action: "import.nifti",
          },
          {
            id: "file.importFolder",
            label: "Import Folder",
            icon: "FolderInput",
            size: "small",
            action: "import.folder",
          },
          {
            id: "file.importDicom",
            label: "Import DICOM",
            icon: "Scan",
            size: "small",
            action: "import.dicom",
            tooltip: "Coming soon",
            disabled: always,
          },
          {
            id: "file.importMask",
            label: "Import Mask",
            icon: "Layers",
            size: "small",
            action: "import.mask",
            disabled: noCase,
          },
        ],
      },
      {
        id: "file.export",
        label: "Export",
        items: [
          {
            id: "file.exportImage",
            label: "Export Image",
            icon: "Image",
            size: "large",
            action: "export.image",
            disabled: noCase,
          },
          {
            id: "file.exportMask",
            label: "Export Mask",
            icon: "Layers2",
            size: "small",
            action: "export.mask",
            disabled: noCase,
          },
          {
            id: "file.exportTable",
            label: "Export Table",
            icon: "Table2",
            size: "small",
            action: "export.table",
            disabled: noResults,
          },
          {
            id: "file.exportReport",
            label: "Export Report",
            icon: "FileText",
            size: "small",
            action: "export.report",
            disabled: noResults,
          },
        ],
      },
      {
        id: "file.application",
        label: "Application",
        items: [
          {
            id: "file.preferences",
            label: "Preferences",
            icon: "Settings",
            size: "large",
            action: "file.preferences",
          },
          {
            id: "file.closeCase",
            label: "Close Case",
            icon: "FolderX",
            size: "small",
            action: "case.close",
            disabled: noCase,
          },
          { id: "file.exit", label: "Exit", icon: "LogOut", size: "small", action: "file.exit" },
        ],
      },
    ],
  },

  // ------------------------------------------------------------------ Home ---
  {
    id: "home",
    label: "Home",
    groups: [
      {
        id: "home.project",
        label: "Project",
        items: [
          // "Load Data" used to sit here too. Its handler was
          // `await handleOpenCase()` — the same function as Open Subject —
          // while its tooltip promised a different behaviour. Two doors to one
          // room is a nuisance; two doors labelled differently is a search for
          // a difference that does not exist.
          {
            id: "home.openCase",
            label: "Open Study",
            icon: "FolderOpen",
            size: "large",
            action: "home.openCase",
            tooltip: "One subject, a folder of subjects, or a case you saved earlier",
          },
          {
            id: "home.newCase",
            label: "New Analysis",
            icon: "FilePlus",
            size: "large",
            action: "home.newCase",
            // It never created a case; Create Case does, further in. The label
            // now says what it does: clear the protocol and start over.
            tooltip: "Start from an empty protocol",
          },
          {
            id: "home.saveProject",
            label: "Save Project",
            icon: "Save",
            size: "large",
            action: "file.saveCase",
            disabled: noCase,
            dropdown: [
              { id: "home.save", label: "Save Case", action: "file.saveCase" },
              { id: "home.saveAs", label: "Save Case As…", action: "case.saveAs" },
            ],
          },
        ],
      },
      {
        id: "home.data",
        label: "Data",
        items: [
          {
            id: "home.openInViewer",
            label: "Open in Viewer",
            icon: "Eye",
            size: "large",
            action: "viewer.open",
            disabled: noCase,
          },
          {
            id: "home.openInFileBrowser",
            label: "Open in File Browser",
            icon: "FolderSearch",
            size: "large",
            action: "shell.revealCase",
            disabled: noCase,
          },
        ],
      },
      {
        id: "home.execute",
        label: "Execute",
        items: [
          RUN,
          STOP,
          {
            id: "exec.pause",
            label: "Pause",
            icon: "Pause",
            size: "large",
            action: "exec.pause",
            tooltip: "Docker jobs cannot be paused mid-stage",
            disabled: always,
          },
        ],
      },
      {
        id: "home.quick",
        label: "Quick Tools",
        items: [
          {
            id: "home.quick.brainMask",
            label: "Brain Mask",
            icon: "Brain",
            size: "large",
            action: "home.quick.brainMask",
            tooltip: "Open Brain Mask and pick a volume",
          },
          {
            id: "home.quick.register",
            label: "Register",
            icon: "Spline",
            size: "large",
            action: "home.quick.register",
            tooltip: "Open DWI → MNI Affine+SyN and pick a volume",
          },
          {
            id: "home.quick.stroke",
            label: "Stroke Seg",
            icon: "Target",
            size: "large",
            action: "home.quick.stroke",
            tooltip: "Open stroke-core segmentation and pick a volume",
          },
          {
            id: "home.quick.quantify",
            label: "Quantify",
            icon: "Ruler",
            size: "large",
            action: "home.quick.quantify",
            tooltip: "Open lesion / HP volumes on Quantification",
          },
        ],
      },
      {
        id: "home.manage",
        label: "Manage",
        items: [
          {
            id: "home.caseDetails",
            label: "Case Details",
            icon: "Info",
            size: "large",
            action: "case.details",
            disabled: noCase,
          },
          {
            id: "home.duplicateCase",
            label: "Duplicate Case",
            icon: "Copy",
            size: "large",
            action: "case.duplicate",
            disabled: noCase,
          },
          {
            id: "home.deleteCase",
            label: "Delete Case",
            icon: "Trash2",
            size: "large",
            action: "case.delete",
            disabled: noCase,
          },
        ],
      },
      {
        id: "home.share",
        label: "Share",
        items: [
          {
            id: "home.exportPackage",
            label: "Export Package",
            icon: "Package",
            size: "large",
            action: "export.package",
            disabled: noCase,
            dropdown: [
              { id: "home.exportPackage.all", label: "Everything", action: "export.package.all" },
              { id: "home.exportPackage.results", label: "Results only", action: "export.package.results" },
              { id: "home.exportPackage.report", label: "Report only", action: "export.package.report" },
            ],
          },
          {
            id: "home.shareCase",
            label: "Share Case",
            icon: "Share2",
            size: "large",
            action: "case.share",
            disabled: noCase,
          },
        ],
      },
      {
        id: "home.tools",
        label: "Tools",
        items: [
          {
            id: "home.addBookmark",
            label: "Add Bookmark",
            icon: "Bookmark",
            size: "large",
            action: "bookmark.add",
            disabled: noCase,
          },
        ],
      },
      {
        id: "home.window",
        label: "Window",
        items: [
          {
            id: "home.layout",
            label: "Layout",
            icon: "LayoutGrid",
            size: "large",
            action: "view.layout.menu",
            dropdown: [
              { id: "home.layout.single", label: "Single", action: "view.layout.single" },
              { id: "home.layout.grid", label: "2 × 2", action: "view.layout.grid" },
              { id: "home.layout.mpr", label: "Axial + Sagittal + Coronal", action: "view.layout.mpr" },
              { id: "home.layout.compare", label: "Compare", action: "view.layout.compare" },
            ],
          },
          {
            id: "home.fullScreen",
            label: "Full Screen",
            icon: "Maximize2",
            size: "large",
            action: "window.fullscreen",
          },
        ],
      },
      {
        id: "home.settings",
        label: "Settings",
        items: [
          {
            id: "home.preferences",
            label: "Preferences",
            icon: "Settings",
            size: "large",
            action: "file.preferences",
            dropdown: [
              { id: "home.preferences.general", label: "General…", action: "file.preferences" },
              { id: "home.preferences.paths", label: "Default Folders…", action: "app.preferences.paths" },
              { id: "home.preferences.docker", label: "Docker & GPU…", action: "app.preferences.docker" },
            ],
          },
        ],
      },
    ],
  },

  // -------------------------------------------------------------- Pipeline ---
  {
    id: "pipeline",
    label: "Pipeline",
    groups: [
      {
        id: "pipeline.case",
        label: "Case",
        items: [
          {
            id: "pipeline.caseSetup",
            label: "Protocol",
            icon: "FolderCog",
            size: "large",
            action: "pipeline.caseSetup",
          },
          {
            id: "pipeline.validateData",
            label: "Validate Data",
            icon: "ShieldCheck",
            size: "large",
            action: "pipeline.validate",
          },
        ],
      },
      {
        id: "pipeline.stages",
        label: "Stages",
        items: [
          {
            id: "pipeline.selectAll",
            label: "Select All",
            icon: "ListChecks",
            size: "small",
            action: "pipeline.selectAllStages",
          },
          { id: "pipeline.clear", label: "Clear", icon: "Eraser", size: "small", action: "pipeline.clearStages" },
          {
            id: "pipeline.runSelected",
            label: "Run Selected",
            icon: "CirclePlay",
            size: "small",
            action: "pipeline.runSelected",
            disabled: cannotRun,
          },
          {
            id: "pipeline.restartFailed",
            label: "Restart Failed",
            icon: "RotateCcw",
            size: "small",
            action: "pipeline.restartFailed",
            disabled: cannotRun,
          },
        ],
      },
      { id: "pipeline.execution", label: "Execution", items: [RUN, STOP] },
      {
        id: "pipeline.config",
        label: "Config",
        items: [
          {
            id: "pipeline.editParameters",
            label: "Edit Parameters",
            icon: "SlidersHorizontal",
            size: "large",
            action: "config.edit",
            disabled: noCase,
          },
          {
            id: "pipeline.openConfig",
            label: "Open Config",
            icon: "FileCode2",
            size: "small",
            action: "config.open",
            disabled: noCase,
          },
        ],
      },
      {
        id: "pipeline.output",
        label: "Output",
        items: [
          {
            id: "pipeline.openOutput",
            label: "Open Output",
            icon: "FolderOpen",
            size: "large",
            action: "pipeline.openOutput",
            disabled: noCase,
          },
          {
            id: "pipeline.refresh",
            label: "Refresh",
            icon: "RefreshCw",
            size: "small",
            action: "output.refresh",
            disabled: noCase,
          },
        ],
      },
    ],
  },

  // ------------------------------------------------------------------ View ---
  {
    id: "view",
    label: "View",
    groups: [
      {
        id: "view.layout",
        label: "Layout",
        items: [
          { id: "view.layout.single", label: "Single", icon: "Square", size: "large", action: "view.layout.single" },
          { id: "view.layout.grid", label: "2 × 2", icon: "Grid2x2", size: "large", action: "view.layout.grid" },
          {
            id: "view.layout.mpr",
            label: "Axial+Sag+Cor",
            icon: "Columns3",
            size: "large",
            action: "view.layout.mpr",
          },
          {
            id: "view.layout.compare",
            label: "Compare",
            icon: "GitCompare",
            size: "large",
            action: "view.layout.compare",
          },
        ],
      },
      {
        id: "view.orientation",
        label: "Orientation",
        items: [
          { id: "view.axial", label: "Axial", icon: "AlignHorizontalJustifyCenter", size: "large", action: "view.orientation.axial" },
          { id: "view.sagittal", label: "Sagittal", icon: "AlignVerticalJustifyCenter", size: "large", action: "view.orientation.sagittal" },
          { id: "view.coronal", label: "Coronal", icon: "AlignCenterVertical", size: "large", action: "view.orientation.coronal" },
          { id: "view.resetView", label: "Reset View", icon: "RotateCw", size: "large", action: "view.reset" },
        ],
      },
      {
        id: "view.display",
        label: "Display",
        items: [
          {
            id: "view.overlay",
            label: "Overlay",
            icon: "ToggleLeft",
            size: "large",
            action: "view.overlay.toggle",
            toggle: true,
          },
          {
            id: "view.opacity",
            label: "Opacity",
            icon: "Droplet",
            size: "large",
            action: "view.opacity.menu",
            dropdown: [
              { id: "view.opacity.25", label: "25 %", action: "view.opacity.25" },
              { id: "view.opacity.50", label: "50 %", action: "view.opacity.50" },
              { id: "view.opacity.70", label: "70 %", action: "view.opacity.70" },
              { id: "view.opacity.100", label: "100 %", action: "view.opacity.100" },
            ],
          },
          {
            id: "view.crosshairs",
            label: "Crosshairs",
            icon: "Crosshair",
            size: "large",
            action: "view.crosshairs.toggle",
            toggle: true,
          },
          {
            id: "view.windowLevel",
            label: "Window/Level",
            icon: "Contrast",
            size: "large",
            action: "view.windowLevel",
          },
          {
            id: "view.autoContrast",
            label: "Auto Contrast",
            icon: "Sun",
            size: "large",
            action: "view.autoContrast",
          },
          {
            id: "view.colormap",
            label: "Colormap",
            icon: "Palette",
            size: "large",
            action: "view.colormap.menu",
            dropdown: [
              { id: "view.colormap.gray", label: "Gray", action: "view.colormap.gray" },
              { id: "view.colormap.warm", label: "Hot (Red-Yellow)", action: "view.colormap.warm" },
              { id: "view.colormap.cool", label: "Cool", action: "view.colormap.cool" },
              { id: "view.colormap.jet", label: "Jet", action: "view.colormap.jet" },
            ],
          },
        ],
      },
      {
        id: "view.navigation",
        label: "Navigation",
        items: [
          {
            id: "view.linkViews",
            label: "Link Views",
            icon: "Link2",
            size: "large",
            action: "view.linkViews.toggle",
            toggle: true,
          },
          {
            id: "view.goToCoordinate",
            label: "Go to Coordinate",
            icon: "Navigation",
            size: "large",
            action: "view.goToCoordinate",
          },
          { id: "view.zoom", label: "Zoom", icon: "ZoomIn", size: "large", action: "view.zoom" },
          { id: "view.pan", label: "Pan", icon: "Hand", size: "large", action: "view.pan" },
        ],
      },
      {
        id: "view.tools",
        label: "Tools",
        items: [
          {
            id: "view.captureSnapshot",
            label: "Capture Snapshot",
            icon: "Camera",
            size: "large",
            action: "view.snapshot",
          },
          {
            id: "view.addBookmark",
            label: "Add Bookmark",
            icon: "Bookmark",
            size: "large",
            action: "bookmark.add",
          },
        ],
      },
      {
        id: "view.panels",
        label: "Panels",
        items: [
          {
            id: "view.panel.studyExplorer",
            label: "Study Explorer",
            icon: "PanelLeft",
            size: "small",
            action: "panel.toggle.left",
            toggle: true,
          },
          {
            id: "view.panel.inspector",
            label: "Inspector",
            icon: "PanelRight",
            size: "small",
            action: "panel.toggle.right",
            toggle: true,
          },
          {
            id: "view.panel.jobs",
            label: "Jobs",
            icon: "ListTodo",
            size: "small",
            action: "dock.show.jobs",
          },
          {
            id: "view.panel.log",
            label: "Log",
            icon: "ScrollText",
            size: "small",
            action: "dock.show.log",
          },
        ],
      },
    ],
  },

  // --------------------------------------------------------------- Results ---
  {
    id: "results",
    label: "Results",
    groups: [
      {
        id: "results.view",
        label: "View",
        items: [
          {
            id: "results.summary",
            label: "Results Summary",
            icon: "LayoutDashboard",
            size: "large",
            action: "results.show.summary",
            disabled: noResults,
          },
          {
            id: "results.report",
            label: "Report",
            icon: "FileText",
            size: "large",
            action: "results.show.report",
            disabled: noResults,
          },
          {
            id: "results.timeSeries",
            label: "Time Series",
            icon: "ChartLine",
            size: "large",
            action: "results.show.timeSeries",
            disabled: noResults,
          },
          {
            id: "results.regionalTable",
            label: "Regional Table",
            icon: "Table2",
            size: "large",
            action: "results.show.regional",
            disabled: noResults,
          },
          {
            id: "results.qcMetrics",
            label: "QC Metrics",
            icon: "Gauge",
            size: "large",
            action: "results.show.qc",
            disabled: noResults,
          },
          {
            id: "results.snapshots",
            label: "Snapshots",
            icon: "Images",
            size: "large",
            action: "results.show.snapshots",
            disabled: noResults,
          },
          {
            id: "results.compare",
            label: "Compare",
            icon: "GitCompare",
            size: "large",
            action: "results.show.compare",
            disabled: noResults,
          },
        ],
      },
      {
        id: "results.report",
        label: "Report",
        items: [
          {
            id: "results.generateReport",
            label: "Generate Report",
            icon: "FilePlus2",
            size: "large",
            action: "results.generateReport",
            disabled: noResults,
          },
          {
            id: "results.editReport",
            label: "Edit Report",
            icon: "SquarePen",
            size: "large",
            action: "report.edit",
            disabled: noResults,
          },
          {
            id: "results.addNote",
            label: "Add Note",
            icon: "StickyNote",
            size: "large",
            action: "report.addNote",
            disabled: noCase,
          },
        ],
      },
      {
        id: "results.export",
        label: "Export",
        items: [
          {
            id: "results.exportPdf",
            label: "Export PDF",
            icon: "FileType2",
            size: "large",
            action: "export.pdf",
            disabled: noResults,
          },
          {
            id: "results.exportCsv",
            label: "Export CSV",
            icon: "Sheet",
            size: "large",
            action: "export.csv",
            disabled: noResults,
          },
          {
            id: "results.exportNifti",
            label: "Export NIfTI",
            icon: "FileImage",
            size: "large",
            action: "export.nifti",
            disabled: noResults,
          },
          {
            id: "results.exportJson",
            label: "Export JSON",
            icon: "FileJson",
            size: "large",
            action: "export.json",
            disabled: noResults,
          },
        ],
      },
      {
        id: "results.actions",
        label: "Actions",
        items: [
          {
            id: "results.refresh",
            label: "Refresh Results",
            icon: "RefreshCw",
            size: "large",
            action: "results.refresh",
            disabled: noCase,
          },
          {
            id: "results.reprocess",
            label: "Reprocess",
            icon: "History",
            size: "large",
            action: "results.reprocess",
            disabled: cannotRun,
          },
        ],
      },
    ],
  },

  // ------------------------------------------------------------- Developer ---
  {
    id: "developer",
    label: "Developer",
    groups: [
      {
        id: "developer.engine",
        label: "Engine",
        items: [
          {
            id: "developer.engineStatus",
            label: "Engine",
            icon: "Cpu",
            size: "large",
            action: "dev.engine.setup",
            tooltip: "Where the pipeline runs, and how to set it up",
          },
          {
            id: "developer.engineVerify",
            label: "Re-check",
            icon: "RefreshCw",
            size: "small",
            action: "dev.engine.verify",
          },
        ],
      },
      {
        id: "developer.docker",
        label: "Docker",
        items: [
          {
            id: "developer.dockerStatus",
            label: "Status",
            icon: "Container",
            size: "large",
            action: "dev.docker.status",
          },
          { id: "developer.dockerImage", label: "Image", icon: "Box", size: "small", action: "dev.docker.image" },
          {
            id: "developer.dockerPull",
            label: "Pull Image",
            icon: "CloudDownload",
            size: "small",
            action: "dev.docker.pull",
          },
          {
            id: "developer.dockerRestart",
            label: "Restart",
            icon: "RefreshCw",
            size: "small",
            action: "dev.docker.restart",
          },
        ],
      },
      {
        id: "developer.gpu",
        label: "GPU",
        items: [
          { id: "developer.gpuDevices", label: "Devices", icon: "Cpu", size: "large", action: "dev.gpu.devices" },
          { id: "developer.cudaStatus", label: "CUDA Status", icon: "Zap", size: "small", action: "dev.gpu.cuda" },
          {
            id: "developer.gpuMemory",
            label: "Memory",
            icon: "MemoryStick",
            size: "small",
            action: "dev.gpu.memory",
          },
        ],
      },
      {
        id: "developer.pipeline",
        label: "Pipeline",
        items: [
          {
            id: "developer.configYaml",
            label: "Config YAML",
            icon: "FileCode2",
            size: "large",
            action: "dev.config.yaml",
          },
          {
            id: "developer.pipelineGraph",
            label: "Pipeline Graph",
            icon: "Workflow",
            size: "large",
            action: "dev.pipeline.graph",
          },
          {
            id: "developer.intermediateFiles",
            label: "Intermediate Files",
            icon: "Files",
            size: "small",
            action: "dev.pipeline.intermediates",
            disabled: noCase,
          },
          {
            id: "developer.cleanup",
            label: "Cleanup",
            icon: "Trash2",
            size: "small",
            action: "dev.cleanup.toggle",
            toggle: true,
            tooltip: "Delete intermediate files when a job finishes",
          },
        ],
      },
      {
        id: "developer.logs",
        label: "Logs",
        items: [
          {
            id: "developer.appLog",
            label: "Application Log",
            icon: "ScrollText",
            size: "small",
            action: "dev.log.app",
          },
          {
            id: "developer.pipelineLog",
            label: "Pipeline Log",
            icon: "FileClock",
            size: "small",
            action: "dev.log.pipeline",
          },
          {
            id: "developer.errorLog",
            label: "Error Log",
            icon: "FileWarning",
            size: "small",
            action: "dev.log.error",
          },
        ],
      },
      {
        id: "developer.debug",
        label: "Debug",
        items: [
          {
            // Explicitly called for in planchat.md §11 — the escape hatch that
            // lets a developer reproduce a run outside the app.
            id: "developer.copyDockerCommand",
            label: "Copy Docker Command",
            icon: "ClipboardCopy",
            size: "large",
            action: "dev.copyDockerCommand",
            tooltip: "Copy the exact `docker run …` argv for this case",
            disabled: noCase,
          },
          {
            id: "developer.openOutput",
            label: "Open Output",
            icon: "FolderOpen",
            size: "small",
            action: "dev.openOutput",
            disabled: noCase,
          },
          {
            id: "developer.copyShellCommand",
            label: "Copy Container Shell Command",
            icon: "Terminal",
            size: "small",
            action: "dev.copyShellCommand",
            disabled: noCase,
          },
          {
            id: "developer.systemInformation",
            label: "System Information",
            icon: "Info",
            size: "small",
            action: "dev.systemInfo",
          },
        ],
      },
    ],
  },
];

const pipelineIdx = CORE_TABS.findIndex((t) => t.id === "pipeline");
export const RIBBON_TABS: RibbonTab[] = [
  ...CORE_TABS.slice(0, pipelineIdx + 1),
  ...toolRibbonTabs(),
  ...CORE_TABS.slice(pipelineIdx + 1),
];

/** Convenience for the shell: the tab strip order without walking groups. */
export const RIBBON_TAB_IDS = RIBBON_TABS.map((t) => t.id);
